import { Controller } from "@hotwired/stimulus"
import ScreenWakeLock from "../helpers/screen_wake_lock"

const MIN_GAP_MS = 60
const MAX_GAP_MS = 30000
const clamp = (number, minimum, maximum) => Math.min(maximum, Math.max(minimum, number))
const toMs = (seconds) => Math.round(Number(seconds || 0) * 1000)

export default class extends Controller {
  static targets = ["list", "recordLabel", "status"]
  static values = { extraId: Number, audioUrl: String, apiBase: String }

  connect() {
    this.audio = document.getElementById("radio-audio") || document.querySelector("audio")
    this.audioContext = null
    this.bufferCache = null
    this.recording = false
    this.pendingStartMs = null
    this.rowGains = new Map()
    this.rowVoices = new Map()
    this.rowPlaybackTokens = new WeakMap()
    this.screenWakeLock = new ScreenWakeLock({ element: this.element })

    this.trackChangeHandler = (event) => {
      if (!this.hasListTarget) return

      const { id, audio_url: audioUrl } = event.detail || {}
      if (id) this.load(id, audioUrl)
    }
    document.addEventListener("fm:trackchange", this.trackChangeHandler)
  }

  disconnect() {
    document.removeEventListener("fm:trackchange", this.trackChangeHandler)
    this.stopAllVoices()
    this.screenWakeLock?.disconnect()
    this.screenWakeLock = null
    this.rowGains.forEach((gain) => {
      try { gain.disconnect() } catch (_error) {}
    })
    this.rowGains.clear()

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close().catch(() => {})
    }
    this.audioContext = null
    this.bufferCache = null
  }

  async load(extraId, audioUrl) {
    if (!this.hasListTarget) return

    this.stopAllVoices()
    this.rowGains.forEach((gain) => {
      try { gain.disconnect() } catch (_error) {}
    })
    this.rowGains.clear()
    this.bufferCache = null
    this.extraIdValue = Number(extraId)
    this.audioUrlValue = audioUrl || ""
    this.resetRecorder()
    this.setStatus("Loading your saved Sips…")

    try {
      const html = await this.request("GET", `/extras/${extraId}/sips`)
      this.listTarget.innerHTML = html
      this.setStatus(this.listTarget.children.length ? "Your Sips are ready." : "No Sips saved on this track yet.")
    } catch (_error) {
      this.setStatus("Could not load your Sips. Sign in again and retry.", "error")
    }
  }

  async toggleRecord(event) {
    const audio = this.audio || document.getElementById("radio-audio") || document.querySelector("audio")
    if (!audio || audio.paused) {
      this.setStatus("Play the track first, then press IN.", "error")
      return
    }

    const nowMs = Math.max(0, toMs(audio.currentTime))
    if (!this.recording) {
      this.pendingStartMs = nowMs
      this.recording = true
      event.currentTarget.setAttribute("aria-pressed", "true")
      if (this.hasRecordLabelTarget) this.recordLabelTarget.textContent = "OUT"
      this.setStatus(`IN marked at ${this.formatSeconds(nowMs / 1000)}.`)
      return
    }

    const durationMs = Number.isFinite(audio.duration) ? toMs(audio.duration) : null
    const startMs = this.pendingStartMs ?? nowMs
    let endMs = Math.max(nowMs, startMs + MIN_GAP_MS)
    endMs = Math.min(endMs, startMs + MAX_GAP_MS)
    if (durationMs) endMs = Math.min(endMs, durationMs)

    if (endMs - startMs < MIN_GAP_MS) {
      this.setStatus("That mark is too close to the end. Move earlier in the track and try again.", "error")
      this.resetRecorder()
      return
    }

    try {
      const html = await this.request("POST", `/extras/${this.extraIdValue}/sips`, {
        start_ms: startMs,
        end_ms: endMs,
        title: ""
      })
      this.appendRow(html)
      this.setStatus("Sip saved. Name it, tune it, or play it below.", "success")
    } catch (_error) {
      this.setStatus("That Sip could not be saved. Please retry.", "error")
    } finally {
      this.resetRecorder()
    }
  }

  async rename(event) {
    const row = this.rowFor(event.currentTarget)
    const id = row?.dataset?.sipsId
    if (!id) return

    try {
      const html = await this.request("PATCH", this.memberPath(id), { title: event.currentTarget.value })
      this.replaceRow(id, html)
      this.setStatus("Sip name saved.", "success")
    } catch (_error) {
      this.setStatus("The Sip name was not saved.", "error")
    }
  }

  async nudgeStart(event) {
    const row = this.rowFor(event.currentTarget)
    const id = row?.dataset?.sipsId
    if (!id) return

    const [startSeconds, endSeconds] = this.boundsFromRow(row)
    const delta = Number.parseInt(event.currentTarget.dataset.delta || "0", 10)
    const startMs = Math.max(0, toMs(startSeconds) + delta)
    const endMs = Math.max(toMs(endSeconds), startMs + MIN_GAP_MS)
    await this.updateBounds(id, startMs, endMs)
  }

  async nudgeEnd(event) {
    const row = this.rowFor(event.currentTarget)
    const id = row?.dataset?.sipsId
    if (!id) return

    const [startSeconds, endSeconds] = this.boundsFromRow(row)
    const delta = Number.parseInt(event.currentTarget.dataset.delta || "0", 10)
    const startMs = Math.max(0, toMs(startSeconds))
    const endMs = Math.max(startMs + MIN_GAP_MS, toMs(endSeconds) + delta)
    await this.updateBounds(id, startMs, endMs)
  }

  async updateBounds(id, startMs, endMs) {
    try {
      const html = await this.request("PATCH", this.memberPath(id), {
        start_ms: startMs,
        end_ms: endMs
      })
      this.replaceRow(id, html)
      this.setStatus("Sip timing saved.", "success")
    } catch (_error) {
      this.setStatus("That timing change could not be saved.", "error")
    }
  }

  async queueToggle(event) {
    const key = event.currentTarget.dataset.key
    if (key !== "reverse") return

    const row = this.rowFor(event.currentTarget)
    const id = row?.dataset?.sipsId
    if (!id) return

    try {
      const html = await this.request("PATCH", this.memberPath(id), {
        reversed: event.currentTarget.checked
      })
      this.replaceRow(id, html)
      this.setStatus("Reverse setting saved.", "success")
    } catch (_error) {
      event.currentTarget.checked = !event.currentTarget.checked
      this.setStatus("The reverse setting was not saved.", "error")
    }
  }

  async setRate(event) {
    const row = this.rowFor(event.currentTarget)
    const id = row?.dataset?.sipsId
    const rate = Number.parseFloat(event.currentTarget.dataset.rate || "")
    if (!id || ![0.5, 1, 2].includes(rate)) return

    try {
      const html = await this.request("PATCH", this.memberPath(id), { speed: rate })
      this.replaceRow(id, html)
      this.setStatus("Sip speed saved.", "success")
    } catch (_error) {
      this.setStatus("The speed setting was not saved.", "error")
    }
  }

  async confirmDestroy(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!window.confirm(event.currentTarget.dataset.confirm || "Delete this Sip?")) return

    const row = this.rowFor(event.currentTarget)
    const id = row?.dataset?.sipsId
    if (!id) return

    const button = event.currentTarget
    const previousLabel = button.textContent
    button.disabled = true
    button.textContent = "…"

    try {
      await this.request("DELETE", this.memberPath(id))
      this.stopRow(row)
      row.remove()
      this.setStatus("Sip deleted.", "success")
    } catch (_error) {
      button.disabled = false
      button.textContent = previousLabel
      this.setStatus("The Sip was not deleted.", "error")
    }
  }

  stop(event) {
    const row = this.rowFor(event.currentTarget)
    if (row) this.stopRow(row)
  }

  async play(event) {
    const row = this.rowFor(event.currentTarget)
    if (!row) return

    // Retrigger this pad without layering voices or reviving a pending start.
    this.stopRow(row)
    const playbackToken = Symbol("sip playback")
    this.rowPlaybackTokens.set(row, playbackToken)
    row.dataset.stopped = ""
    try {
      await this.playRow(row, playbackToken)
    } catch (_error) {
      if (this.rowPlaybackTokens.get(row) !== playbackToken) return
      this.stopRow(row)
      this.setStatus("This Sip could not be played. Reload the track and retry.", "error")
    }
  }

  async playRow(row, playbackToken) {
    const context = await this.ensureAudioContext()
    if (this.rowPlaybackTokens.get(row) !== playbackToken) return
    const buffer = await this.ensureDecodedBuffer()
    if (this.rowPlaybackTokens.get(row) !== playbackToken) return
    const { startSeconds, endSeconds, speed, reversed, infinite } = this.rowState(row)
    const start = clamp(startSeconds, 0, buffer.duration)
    const finish = clamp(endSeconds, 0, buffer.duration)
    if (finish <= start) throw new Error("Invalid Sip bounds")

    const source = context.createBufferSource()
    source.buffer = this.sliceBuffer(buffer, start, finish, reversed)
    source.playbackRate.value = clamp(speed, 0.5, 2)
    source.connect(this.gainForRow(row))

    const voices = this.voicesForRow(row)
    voices.add(source)
    row.dataset.looping = infinite ? "1" : ""
    this.setPlayingState(row, true)

    source.onended = () => {
      try { source.disconnect() } catch (_error) {}
      voices.delete(source)
      if (this.rowPlaybackTokens.get(row) !== playbackToken) return

      if (row.dataset.stopped === "1") {
        this.screenWakeLock?.release(row)
        this.setPlayingState(row, false)
        return
      }

      const nextState = this.rowState(row)
      if (!nextState.infinite) {
        row.dataset.looping = ""
        if (voices.size === 0) {
          this.screenWakeLock?.release(row)
          this.setPlayingState(row, false)
        }
        return
      }

      row.dataset.looping = "1"
      this.playRow(row, playbackToken).catch(() => {
        if (this.rowPlaybackTokens.get(row) !== playbackToken) return
        this.stopRow(row)
        this.setStatus("The loop stopped because the Sip could not be replayed.", "error")
      })
    }

    source.start()
    this.screenWakeLock?.hold(row)
  }

  async ensureAudioContext() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) throw new Error("Web Audio is unavailable")

    this.audioContext ||= new AudioContextClass()
    if (this.audioContext.state === "suspended") await this.audioContext.resume()
    return this.audioContext
  }

  async ensureDecodedBuffer() {
    if (this.bufferCache) return this.bufferCache

    const audioUrl = this.currentAudioUrl()
    if (!audioUrl) throw new Error("Track audio is unavailable")

    const response = await fetch(audioUrl, { credentials: "same-origin" })
    if (!response.ok) throw new Error(`Audio HTTP ${response.status}`)

    const bytes = await response.arrayBuffer()
    const context = await this.ensureAudioContext()
    this.bufferCache = await context.decodeAudioData(bytes)
    return this.bufferCache
  }

  sliceBuffer(buffer, startSeconds, endSeconds, reverse = false) {
    const sampleRate = buffer.sampleRate
    const start = Math.max(0, Math.floor(startSeconds * sampleRate))
    const finish = Math.min(buffer.length, Math.floor(endSeconds * sampleRate))
    const length = finish - start
    if (length <= 0) throw new Error("Empty Sip")

    const output = this.audioContext.createBuffer(buffer.numberOfChannels, length, sampleRate)
    for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
      const inputData = buffer.getChannelData(channel)
      const outputData = output.getChannelData(channel)
      if (reverse) {
        for (let index = 0; index < length; index += 1) {
          outputData[index] = inputData[finish - index - 1]
        }
      } else {
        outputData.set(inputData.subarray(start, finish))
      }
    }
    return output
  }

  gainForRow(row) {
    const id = row.dataset.sipsId
    if (!this.rowGains.has(id)) {
      const gain = this.audioContext.createGain()
      gain.gain.value = Number.parseFloat(row.dataset.volume || "1")
      gain.connect(this.audioContext.destination)
      this.rowGains.set(id, gain)
    }
    return this.rowGains.get(id)
  }

  voicesForRow(row) {
    const id = row.dataset.sipsId
    if (!this.rowVoices.has(id)) this.rowVoices.set(id, new Set())
    return this.rowVoices.get(id)
  }

  stopRow(row) {
    this.rowPlaybackTokens.delete(row)
    row.dataset.stopped = "1"
    row.dataset.looping = ""
    this.screenWakeLock?.release(row)
    const id = row.dataset.sipsId
    const voices = this.rowVoices.get(id) || new Set()
    voices.forEach((voice) => {
      voice.onended = null
      try { voice.stop() } catch (_error) {}
      try { voice.disconnect() } catch (_error) {}
    })
    voices.clear()
    this.setPlayingState(row, false)
  }

  stopAllVoices() {
    this.element.querySelectorAll(".sip-row").forEach((row) => this.stopRow(row))
    this.rowVoices.clear()
  }

  setPlayingState(row, playing) {
    row.querySelector(".tile-play")?.classList.toggle("is-playing", playing)
  }

  rowFor(element) {
    return element?.closest(".sip-row")
  }

  boundsFromRow(row) {
    const read = (selector) => {
      const match = (row.querySelector(selector)?.textContent || "0").match(/[\d.]+/)
      return Number.parseFloat(match?.[0] || "0")
    }
    return [read(".start"), read(".end")]
  }

  rowState(row) {
    const [startSeconds, endSeconds] = this.boundsFromRow(row)
    return {
      startSeconds,
      endSeconds,
      speed: Number.parseFloat(row.querySelector("[data-rate].is-active")?.dataset?.rate || "1"),
      reversed: Boolean(row.querySelector('input[data-key="reverse"]')?.checked),
      infinite: Boolean(row.querySelector('input[data-key="infinite"]')?.checked)
    }
  }

  tapVolume(event) {
    const row = this.rowFor(event.currentTarget)
    if (!row) return

    const rectangle = event.currentTarget.getBoundingClientRect()
    const volume = clamp(1 - ((event.clientY - rectangle.top) / rectangle.height), 0, 1)
    row.dataset.volume = String(volume)
    this.rowGains.get(row.dataset.sipsId)?.gain.setValueAtTime(
      volume,
      this.audioContext?.currentTime || 0
    )
    event.currentTarget.style.setProperty("--vol", `${Math.round(volume * 100)}%`)
    event.currentTarget.classList.toggle("is-muted", volume === 0)
  }

  volStart(event) {
    event.currentTarget.setPointerCapture?.(event.pointerId)
    this.volumeDragTarget = event.currentTarget
    this.tapVolume(event)
  }

  volMove(event) {
    if (this.volumeDragTarget === event.currentTarget) this.tapVolume(event)
  }

  volEnd(event) {
    try { event.currentTarget.releasePointerCapture?.(event.pointerId) } catch (_error) {}
    this.volumeDragTarget = null
  }

  resetRecorder() {
    this.recording = false
    this.pendingStartMs = null
    if (this.hasRecordLabelTarget) this.recordLabelTarget.textContent = "IN"
    this.element.querySelector(".jb-mark-btn")?.setAttribute("aria-pressed", "false")
  }

  appendRow(html) {
    if (!this.hasListTarget) return
    this.listTarget.appendChild(document.createRange().createContextualFragment(html))
  }

  replaceRow(id, html) {
    const current = document.getElementById(`sip-${id}`)
    if (!current) return

    const infinite = current.querySelector('input[data-key="infinite"]')?.checked
    const volume = current.dataset.volume
    this.stopRow(current)

    const fragment = document.createRange().createContextualFragment(html)
    const replacement = fragment.firstElementChild
    if (!replacement) return

    replacement.querySelector('input[data-key="infinite"]')?.toggleAttribute("checked", Boolean(infinite))
    if (volume) {
      replacement.dataset.volume = volume
      const volumePad = replacement.querySelector(".vol-pad")
      volumePad?.style.setProperty("--vol", `${Math.round(Number.parseFloat(volume) * 100)}%`)
    }
    current.replaceWith(replacement)
  }

  currentAudioUrl() {
    if (this.hasAudioUrlValue && this.audioUrlValue) return this.audioUrlValue
    const audio = this.audio || document.querySelector("audio")
    return audio?.currentSrc || audio?.src || ""
  }

  memberPath(id) {
    return `/extras/${this.extraIdValue}/sips/${id}`
  }

  formatSeconds(seconds) {
    return `${Number(seconds).toFixed(3)}s`
  }

  setStatus(message, state = "info") {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.dataset.state = state
  }

  async request(method, path, payload = null) {
    const headers = { "Accept": "text/html" }
    const options = {
      method,
      credentials: "same-origin",
      headers
    }

    if (method !== "GET") {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) throw new Error("Missing CSRF token")
      headers["X-CSRF-Token"] = csrfToken
      headers["Content-Type"] = "application/json"
      if (payload) options.body = JSON.stringify(payload)
    }

    const response = await fetch(this.apiUrl(path), options)
    if (response.redirected) throw new Error("Sip session expired")
    if (!response.ok) throw new Error(`Sip HTTP ${response.status}`)
    return method === "DELETE" ? "" : response.text()
  }

  apiUrl(path) {
    const base = this.hasApiBaseValue && this.apiBaseValue ? this.apiBaseValue : window.location.origin
    return `${base}${path}`
  }
}
